import type { NCNode } from '../../commonTypes';

export type SearchSourceNodeCandidate = Pick<NCNode, 'uid' | 'label' | 'src'>;

function normalizeLabel(value: string): string {
	return value.trim().toLowerCase();
}

function findByUid<T extends SearchSourceNodeCandidate>(nodes: T[], uid?: string): T | null {
	if (!uid) return null;
	return nodes.find((node) => node.uid === uid) || null;
}

/**
 * Resolve the board node a Search panel belongs to. Prefer an explicit source
 * id, then an image match, then an exact label match. Never return a node that
 * is not already on the board.
 */
export function resolveSearchSourceNode<T extends SearchSourceNodeCandidate>({
	nodes,
	selectedNodeIds = [],
	selectedNodeLabels = [],
	searchSrc,
	fallback,
}: {
	nodes: T[];
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	searchSrc?: string;
	fallback?: T | null;
}): T | null {
	for (const id of selectedNodeIds) {
		const match = findByUid(nodes, id);
		if (match) return match;
	}

	const trimmedSrc = searchSrc?.trim();
	if (trimmedSrc) {
		const match = nodes.find((node) => node.src === trimmedSrc);
		if (match) return match;
	}

	for (const label of selectedNodeLabels) {
		const target = normalizeLabel(label);
		if (!target) continue;
		const match = nodes.find((node) => normalizeLabel(node.label || '') === target);
		if (match) return match;
	}

	return findByUid(nodes, fallback?.uid);
}

export function searchPanelSourceFields(
	node: Pick<SearchSourceNodeCandidate, 'uid' | 'label'> | null
): { selectedNodeIds?: string[]; selectedNodeLabels?: string[] } {
	if (!node) return {};
	return {
		selectedNodeIds: [node.uid],
		...(node.label ? { selectedNodeLabels: [node.label] } : {}),
	};
}
